/* eslint-disable max-len */
/* eslint-disable import/no-unresolved */
import { isUndefined, sortBy } from 'lodash';
import uuid from 'react-uuid';
import { Investment, SecurityModel } from 'api';
import { securityTypes } from 'common/constants';
import {
  ACCRUED_DIVIDENDS_ROW_NUMBER,
  COMPOUNDING_PERIOD_ALIAS,
  COMPOUNDING_PERIOD_ROW_NUMBER,
  COMPOUNDING_ROW_NUMBER, CONVERSION_RATE_ROW_NUMBER,
  CURRENT_OWNERSHIP_ROW_NUMBER, DIVIDEND_PAYMENT_TYPE_ROW_NUMBER,
  DIVIDEND_RATE_LEDGER_ROW_NUMBER,
  DIVIDEND_RATE_ROW_NUMBER,
  DIVIDEND_RATES,
  divRateLedgerInfluencedCellAliases,
  DRL_COMPOUNDING_PERIODS_ALIAS,
  FULLY_DILUTED_OWNERSHIP_ROW_NUMBER,
  HAS_MULTIPLE_INVESTMENT_DATES_ROW_NUMBER,
  INITIAL_LIQUIDATION_PREFERENCE_ROW_NUMBER,
  INVESTMENT_DATE_ROW_NUMBER,
  ISSUE_PRICE_ROW_NUMBER,
  liqPrefLedgerInfluencedCellAliases,
  LIQUIDATION_PREFERENCE_LEDGER_ALIAS,
  LIQUIDATION_PREFERENCE_LEDGER_ROW_NUMBER,
  LIQUIDATION_PREFERENCE_ROW_NUMBER,
  MULTIPLE_INVESTMENT_LEDGER_ALIAS,
  MULTIPLE_INVESTMENT_LEDGER_ROW_NUMBER,
  OPTIONS_LEDGER_ROW_NUMBER,
  PIK_SHARES_ROW_NUMBER,
  SECURITY_NAME_ROW_NUMBER,
  SECURITY_SELECTION_DISABLED_BECAUSE_OF_FO,
  SECURITY_TYPE_ROW_NUMBER,
  SHARES_FULLY_DILUTED_ROW_NUMBER,
  SHARES_OUTSTANDING_ROW_NUMBER,
  STRIKE_PRICE_ROW_NUMBER,
  TOTAL_PREFERENCE_ROW_NUMBER
} from 'common/constants/cap-table';
import { YES_STRING } from 'common/constants/general';
import { CONVERTIBLE_NOTES, PREFERRED_STOCK, UNISSUED_OPTIONS } from 'common/constants/securityTypes';
import { Row } from 'common/types/scalarSpreadsheet';
import { CellValue } from 'models/interfaces/spreadsheet-types/core-types';
import checkIfExistConvertibleNoteInCapTable from 'pages/CapTable/cap-table/utilities/checkIfExistConvertibleNoteInCapTable';
import { alphabetGenerator, parseValue, toString } from 'utilities';
// The alias <-> metadata-key <-> reference-synonym translation lives in
// aiFieldMap.ts (ONE source of truth, shared with securityAiPatches' store
// accept/dismiss patches): metadata keyed by MODEL field names must attach to
// CELL-alias rows here, and the store patches invert the same table.
import { AI_METADATA_KEY_BY_ALIAS, REFERENCE_FIELD_SYNONYMS_BY_ALIAS } from './aiFieldMap';
import normalizeMultipleInvestmentsLedger from './normalizeMultipleInvestmentsLedger';
import readLiquidationPreferenceConfig from './readLiquidationPreferenceConfig';
import { LedgerConfig } from './readLiquidationPreferenceConfig.props';
import { handleLedgerValue } from './variablePropertiesUtils';
import { CapTableReferenceGenerator, MappingReferenceData } from '../../../../services/reference-generator';
import { CapTableCell, CapTableRow } from '../components/captable-table/CaptableTable.props';
import {
  BASIS_3_VALUE,
  PRICE_PLUS_DIVIDENDS_VALUE,
} from '../components/liquidation-preference-ledger/liq-pref-ledger-table-config/constants';

const influenceCellsWithInformationFromDivRateLedger = ({
  column,
  columnLegend,
  cells,
}: {
  column: SecurityModel;
  columnLegend: string;
  cells: Record<string, CapTableCell>;
}) => {
  divRateLedgerInfluencedCellAliases.forEach(ledgerInfluencedCell => {
    const currentCellKey = `${columnLegend}${ledgerInfluencedCell.rowNumber}`;
    const aliasToUse =
      ledgerInfluencedCell.alias === COMPOUNDING_PERIOD_ALIAS
        ? DRL_COMPOUNDING_PERIODS_ALIAS
        : ledgerInfluencedCell.alias;
    const ledgerValuesForAlias = (column.dividend_rate_ledger_items ?? []).map(
      ledgerItem => ledgerItem[aliasToUse as keyof typeof ledgerItem]
    );
    const cellType = cells[`${currentCellKey}`].dbType;

    const newValueForCell = handleLedgerValue(ledgerValuesForAlias, cellType as CellValue);

    Object.assign(cells[`${currentCellKey}`], { expr: newValueForCell, value: newValueForCell });
  });
};

const influenceCellsWithInformationFromLiqPrefLedger = ({
  column,
  columnLegend,
  cells,
  hasLiquidationPreferenceLedger,
}: {
  column: SecurityModel;
  columnLegend: string;
  cells: Record<string, CapTableCell>;
  hasLiquidationPreferenceLedger: boolean;
}) => {
  liqPrefLedgerInfluencedCellAliases.forEach(ledgerInfluencedCell => {
    const currentCellKey = `${columnLegend}${ledgerInfluencedCell.rowNumber}`;
    const ledgerValuesForAlias = column.liquidation_preference_ledger_config?.items?.map(
      ledgerItem => ledgerItem[ledgerInfluencedCell.alias as keyof typeof ledgerItem]
    );
    const cellType = cells[`${currentCellKey}`].dbType;
    const { defaultValue } = cells[`${currentCellKey}`];

    const newValueForCell = hasLiquidationPreferenceLedger
      ? handleLedgerValue(ledgerValuesForAlias as any[], cellType as CellValue)
      : defaultValue;

    Object.assign(cells[`${currentCellKey}`], { expr: newValueForCell, value: newValueForCell });
  });
};

const updateExpressionWithVariableLiqPref = (
  cell: CapTableCell,
  column: SecurityModel,
  measurementDate: string,
  expressionRowNumber: number
) => {
  const { expr, columnLegend } = cell;
  let updatedExpr = expr as string;
  const { liquidation_preference_ledger_config, shares_outstanding, investment_date, issue_price } = column;
  const {
    liquidationPreference,
    dividendRate,
    compoundingPeriod,
    hasCompounding,
    yearfracBasis,
    hasDividends,
    multipleOf,
  } = readLiquidationPreferenceConfig(
    liquidation_preference_ledger_config as unknown as LedgerConfig,
    measurementDate,
    investment_date as string,
    issue_price as string,
    shares_outstanding as string
  );
  const dividendRateKey = `${columnLegend}${DIVIDEND_RATE_ROW_NUMBER}`;
  const hasCompoundingKey = `${columnLegend}${COMPOUNDING_ROW_NUMBER}`;
  const compoundingPeriodKey = `${columnLegend}${COMPOUNDING_PERIOD_ROW_NUMBER}`;
  const liquidationPreferenceKey = `${columnLegend}${LIQUIDATION_PREFERENCE_ROW_NUMBER}`;
  const accruedDividendsKey = `${columnLegend}${ACCRUED_DIVIDENDS_ROW_NUMBER}`;

  switch (expressionRowNumber) {
    case ACCRUED_DIVIDENDS_ROW_NUMBER:
      if (dividendRate) {
        updatedExpr = updatedExpr.replaceAll(dividendRateKey, `${dividendRate}`);
      }
      if (compoundingPeriod) {
        updatedExpr = updatedExpr.replaceAll(hasCompoundingKey, `${hasCompounding}`);
        updatedExpr = updatedExpr.replaceAll(compoundingPeriodKey, `${compoundingPeriod}`);
      }

      break;
    case INITIAL_LIQUIDATION_PREFERENCE_ROW_NUMBER:
      if (liquidationPreference) {
        updatedExpr = updatedExpr.replaceAll(liquidationPreferenceKey, `${liquidationPreference}`);
      }
      break;
    case TOTAL_PREFERENCE_ROW_NUMBER:
      if (liquidationPreference && multipleOf === PRICE_PLUS_DIVIDENDS_VALUE && hasDividends) {
        // do liquidation preference times the dividends in the formula
        updatedExpr = updatedExpr.replaceAll(accruedDividendsKey, `${liquidationPreference}*${accruedDividendsKey}`);
      }
      break;
    default:
      break;
  }
  return replaceYearFracBasis(updatedExpr, yearfracBasis);
};

export const calcSharesOutstandingFromMultipleInvestments = (investments: Investment[]) =>
  investments.map(investment => Number(investment.shares)).reduce((previous, current) => previous + current, 0);

const getExpr = (expr: string, columnLegend: string, defaultValue = '') =>
  expr ? expr.replace(/@/g, columnLegend) : defaultValue;

const replaceYearFracBasis = (expr: string, basis: number = BASIS_3_VALUE) =>
  expr.replaceAll(/yearfracBasis/g, basis.toString());

const capTableParser = async ({
  columns,
  rowConfig,
  tableData,
}: {
  columns: SecurityModel[];
  rowConfig: Row[];
  tableData: {
    existingHiddenNoteColumns: SecurityModel[];
    measurementDate: string;
    has_liquidation_preference_ledger: boolean;
    has_dividend_rate_ledger: boolean;
  };
}) => {
  const { measurementDate, has_liquidation_preference_ledger: hasLiquidationPreferenceLedger } = tableData;
  const alphabet = alphabetGenerator([], columns.length);
  const cells: Record<string, CapTableCell> = {};

  // Sort all columns by creation date
  const sortedColumns = sortBy(columns, ['order']);

  // Filter updated columns to get only not "Pool Option" security types
  const noConvertibleNotesEitherUnissuedOption = (rowNumber: number) =>
    sortedColumns.reduce((list: string[], column: SecurityModel, columnIndex: number) => {
      const key = alphabet[columnIndex] + rowNumber;

      if (
        ![toString(securityTypes.UNISSUED_OPTIONS), toString(securityTypes.CONVERTIBLE_NOTES)].includes(
          column.security_type?.toString() ?? ''
        )
      ) {
        list.push(key);
      }

      return list;
    }, []);

  const notOptionPoolKeyList = (rowNumber: number) =>
    sortedColumns.reduce((list: string[], column: SecurityModel, columnIndex: number) => {
      const key = alphabet[columnIndex] + rowNumber;

      if (column.security_type !== securityTypes.UNISSUED_OPTIONS) {
        list.push(key);
      }

      return list;
    }, []);

  const currentOwnershipCellFormula = (columnLegend: string) => {
    const key = columnLegend + SHARES_FULLY_DILUTED_ROW_NUMBER;
    const captableHasConvertibleNotes = checkIfExistConvertibleNoteInCapTable(sortedColumns);
    const keyList = captableHasConvertibleNotes
      ? noConvertibleNotesEitherUnissuedOption(SHARES_FULLY_DILUTED_ROW_NUMBER)
      : notOptionPoolKeyList(SHARES_FULLY_DILUTED_ROW_NUMBER);

    if (keyList.length === 0) {
      return '0.0';
    }
    return `=${key}/sum(${keyList})`;
  };

  const fullyDilutedCellFormula = (columnLegend: string) => {
    const key = columnLegend + SHARES_FULLY_DILUTED_ROW_NUMBER;
    const keyList = columns.map((_c, columnIndex) => alphabet[columnIndex] + SHARES_FULLY_DILUTED_ROW_NUMBER);

    if (keyList.length === 0) {
      return '0';
    }

    return `=${key}/sum(${keyList})`;
  };

  const getLinkedCells = (linkedCells: string[], columnLegend: string, columnLegends: string[]) =>
    new Set(
      linkedCells?.flatMap(linkedCell => {
        if (linkedCell.includes('@@')) {
          return columnLegends.map(legend => linkedCell.replace('@@', legend));
        }
        return linkedCell.replace('@', columnLegend);
      })
    );

  const tooltipForSecurityInFundOwnership = (
    pageIsNotDisabledAndSecurityInFundOwnership: boolean,
    rowTooltipMessages: string[] | null | undefined
  ) => {
    if (pageIsNotDisabledAndSecurityInFundOwnership) {
      return [SECURITY_SELECTION_DISABLED_BECAUSE_OF_FO];
    }
    return rowTooltipMessages;
  };

  /**
   * Updates a specific property of a CapTableCell in the provided cells record.
   *
   * This function directly mutates the cells object by updating the specified field
   * of the cell identified by cellKey with the provided value.
   *
   * @param capTableCells - The record containing all CapTableCell objects indexed by their keys
   * @param field - The property of CapTableCell to update (e.g., 'expr', 'value', 'id', etc.)
   * @param valueToReplace - The new value to assign to the specified field
   * @param cellKey - The key identifying the specific cell to update in the capTableCells record
   *
   * @example
   * ```typescript
   * // Update the expression of a specific cell
   * updateCellProp(cells, 'expr', '=SUM(A1:A10)', 'A1');
   *
   * // Update the value of a cell
   * updateCellProp(cells, 'value', 100, 'B2');
   * ```
   */
  const updateCellProp = (
    capTableCells: Record<string, CapTableCell>,
    field: keyof CapTableCell,
    valueToReplace: CellValue,
    cellKey: string
  ) => {
    // eslint-disable-next-line no-param-reassign
    capTableCells[cellKey][field] = valueToReplace;
  };

  const handleConvertibleNotes = (column: SecurityModel, capTableCells: Record<string, CapTableCell>, key: string) => {
    if (column.security_type === CONVERTIBLE_NOTES) {
      const expr = toString(
        column.convertible_notes.reduce((sofar, { expected_shares }) => sofar + Number(expected_shares), 0)
      );
      updateCellProp(capTableCells, 'expr', expr, key);
    }
  };

  const updateFirmOwnershipPercentage = (cell: CapTableCell, rowConfig: Row[]) => {
    const {columnLegend} = cell;
    const fundOwnershipRowNumbers = rowConfig
      .filter(row => row.alias?.includes('fund_ownership_percentage_'))
      .map(row => rowConfig.findIndex(r => r.alias === row.alias) + 1);
    const fundOwnershipKeys = fundOwnershipRowNumbers.map(
      rowNumber => `${columnLegend}${rowNumber}`
    );
    const expr = `=sum(${fundOwnershipKeys})`;
    // eslint-disable-next-line no-param-reassign
    cell.expr = expr;
  }

  const updateFirmShare = (cell: CapTableCell, rowConfig: Row[]) => {
    const {columnLegend} = cell;
    const fundShareRowNumbers = rowConfig
      .filter(row => row.alias?.includes('fund_shares_'))
      .map(row => rowConfig.findIndex(r => r.alias === row.alias) + 1);
    const fundShareKeys = fundShareRowNumbers.map(
      rowNumber => `${columnLegend}${rowNumber}`
    );
    const expr = `=sum(${fundShareKeys})`;
    // eslint-disable-next-line no-param-reassign
    cell.expr = expr;
  }

  const updateFundOwnershipPercentage = (cell: CapTableCell, columns: SecurityModel[], rowConfig: Row[]) => {
  //  expr should be the related fund_share cell key divided by the sum of all of the fund_share cell keys
    const fullyDilutedRowNumber = SHARES_FULLY_DILUTED_ROW_NUMBER;
    // needs to map based on the specific fund rather than just the first one.
    const {fundId} = cell.data
    const fundShareRowNumber = rowConfig.findIndex(row => row.alias === `fund_shares_${  fundId}` ) + 1;
    const columnLegends = alphabetGenerator([], columns.length);
    const fundShareKeys = columns.map((column, columnIndex) => columnLegends[columnIndex] + fullyDilutedRowNumber);
    const fundShareKey = cell.columnLegend + fundShareRowNumber;
    const expr = `=${fundShareKey}/sum(${fundShareKeys})`;
    // eslint-disable-next-line no-param-reassign
    cell.expr = expr;
  }

  const updateFundShareValue = (cell: CapTableCell, column: SecurityModel) => {
    //   we need to find the interesection of the security we are on and fund ownership details we are looking at

    const isNote = column.convertible_notes.length > 0;
    let fodForSecurity = [];
    if (isNote && tableData.existingHiddenNoteColumns) {
      const existingHiddenNoteColumnsMap = tableData.existingHiddenNoteColumns.reduce(
        (acc, column) => {
          acc[column.id] = column;
          return acc;
        },
          {} as Record<string, SecurityModel>
      );
      // then we are looking for the shares as converted so we need to mulitpy that number conversion rate for the column
      // and then add the total shares divided by teh shares outstanding and then multiply by the cash or pik
      fodForSecurity = (cell.data?.fodList ?? []).filter(fod => existingHiddenNoteColumnsMap[fod.security]);
    } else {
      fodForSecurity = (cell.data?.fodList ?? []).filter(fod => fod.security === cell.columnId)
    }
    // DIVIDEND_PAYMENT_TYPE_ROW_NUMBER - cash or pik
    // CONVERSION_RATE_ROW_NUMBER - conversion rate
    // SHARES_OUTSTANDING_ROW_NUMBER - shares outstanding

    const {columnLegend} = cell;
    const cashOrPikKey = `${columnLegend}${DIVIDEND_PAYMENT_TYPE_ROW_NUMBER}`;
    const conversionRateKey = `${columnLegend}${CONVERSION_RATE_ROW_NUMBER}`;
    const sharesOutstandingKey = `${columnLegend}${SHARES_OUTSTANDING_ROW_NUMBER}`;
    const pikSharesKey = `${columnLegend}${PIK_SHARES_ROW_NUMBER}`;

    // then we are looking for the shares as converted so we need to mulitpy that number conversion rate for the column
    // and then add the total shares divided by teh shares outstanding and then multiply by the cash or pik
    const totalShares = fodForSecurity?.reduce((acc, curr) => acc + Number(curr.shares), 0);
    const expr = `=${totalShares} * ${conversionRateKey} + (${totalShares}/${sharesOutstandingKey} * ${pikSharesKey} * ${cashOrPikKey})`;
    // eslint-disable-next-line no-param-reassign
    cell.expr = expr;
  }

  const updateFundLiqPref = (cell: CapTableCell, rowConfig: Row[]) => {
    // only do this if there are no additional investments and the fund doesn't include multiple investment dates?
    const {columnLegend} = cell;
    const hasMultipleInvestmentDatesRowNumber = HAS_MULTIPLE_INVESTMENT_DATES_ROW_NUMBER
    const fullyDilutedRowNumber = SHARES_FULLY_DILUTED_ROW_NUMBER;
    const {fundId} = cell.data
    const fundShareRowNumber = rowConfig.findIndex(row => row.alias === `fund_shares_${  fundId}` ) + 1;
    // fund shares over filly diluted times total preference
    const totalPreferenceRowNumber = TOTAL_PREFERENCE_ROW_NUMBER;
    const expr = `=IF(${columnLegend}${hasMultipleInvestmentDatesRowNumber} ==  1, 'Excl.',
        (${columnLegend}${fundShareRowNumber}/${columnLegend}${fullyDilutedRowNumber}) * ${columnLegend}${totalPreferenceRowNumber}
    )`;
    // eslint-disable-next-line no-param-reassign
    cell.expr = expr;
  }

  const updateFirmLiqPref = (cell: CapTableCell, rowConfig: Row[]) => {
    const {columnLegend} = cell;
    const fundLiqPrefRowNumbers = rowConfig
      .filter(row => row.alias?.includes('fund_liquidation_preference_'))
      .map(row => rowConfig.findIndex(r => r.alias === row.alias) + 1);
    const fundLiqPrefKeys = fundLiqPrefRowNumbers.map(
      rowNumber => `${columnLegend}${rowNumber}`
    );
    const expr = `=sum(${fundLiqPrefKeys})`;
    // eslint-disable-next-line no-param-reassign
    cell.expr = expr;
  }

  const handleSecurityType = (
    securityType: CapTableCell,
    columnLegend: string,
    capTableCells: Record<string, CapTableCell>,
    key: string
  ) => {
    if (securityType?.value !== UNISSUED_OPTIONS) {
      updateCellProp(capTableCells, 'expr', getExpr(`=IF(@${DIVIDEND_PAYMENT_TYPE_ROW_NUMBER}==Pik,@${ACCRUED_DIVIDENDS_ROW_NUMBER}/@${ISSUE_PRICE_ROW_NUMBER},0)`, columnLegend), key);
    }
  };

  const handlePreferredStockWithVariableLiqPref = (
    newCell: CapTableCell,
    column: SecurityModel,
    measurementDateStr: string,
    capTableCells: Record<string, CapTableCell>,
    key: string
  ) => {
    updateCellProp(
      capTableCells,
      'expr',
      updateExpressionWithVariableLiqPref(newCell, column, measurementDateStr, ACCRUED_DIVIDENDS_ROW_NUMBER),
      key
    );
  };

  const handlePreferredStockWithMultipleDividendRates = (capTableCells: Record<string, CapTableCell>, key: string) => {
    updateCellProp(capTableCells, 'expr', undefined, key);
    updateCellProp(capTableCells, 'altExpr', undefined, key);
  };

  const handleInitialLiquidationPreference = (
    preferredWithVariableLiqPref: boolean,
    newCell: CapTableCell,
    column: SecurityModel,
    measurementDateStr: string,
    capTableCells: Record<string, CapTableCell>,
    key: string
  ) => {
    if (preferredWithVariableLiqPref) {
      updateCellProp(
        capTableCells,
        'expr',
        updateExpressionWithVariableLiqPref(
          newCell,
          column,
          measurementDateStr,
          INITIAL_LIQUIDATION_PREFERENCE_ROW_NUMBER
        ),
        key
      );
    }
  };

  const handleTotalPreference = (
    preferredWithVariableLiqPref: boolean,
    newCell: CapTableCell,
    column: SecurityModel,
    measurementDateStr: string,
    capTableCells: Record<string, CapTableCell>,
    key: string
  ) => {
    if (preferredWithVariableLiqPref) {
      updateCellProp(
        capTableCells,
        'expr',
        updateExpressionWithVariableLiqPref(newCell, column, measurementDateStr, TOTAL_PREFERENCE_ROW_NUMBER),
        key
      );
    }
  };

  // Parse body cells
  sortedColumns.forEach((column: SecurityModel, columnIndex: number) => {
    const columnLegend = alphabet[columnIndex];

    const preferredWithVariableLiqPref = Boolean(
      column.security_type === PREFERRED_STOCK && column?.liquidation_preference_ledger_config?.items?.length
    );
    const preferredWithMultipleDividendRates = Boolean(
      column.security_type === PREFERRED_STOCK && column[DIVIDEND_RATES]?.length
    );
    const currentOwnershipExpr = currentOwnershipCellFormula(columnLegend);
    const fullyDilutedExpr = fullyDilutedCellFormula(columnLegend);

    const shouldBreakCaseLiqPrefRow = !column.liquidation_preference_ledger_config || !preferredWithVariableLiqPref;

    const securityType = cells[columnLegend + SECURITY_TYPE_ROW_NUMBER];

    rowConfig.forEach((row: CapTableRow, index: number) => {
      const rowNumber = index + 1;
      const key = columnLegend + rowNumber;
      const columnValue = column[row.alias as keyof SecurityModel];
      let value = !isUndefined(columnValue) ? columnValue : null;
      if (row.alias === MULTIPLE_INVESTMENT_LEDGER_ALIAS) {
        value = normalizeMultipleInvestmentsLedger(value, column.has_multiple_investments);
      }
      const type = row.gridType || null;

      const ref = column.security_ref || uuid();
      const { defaultValue } = row;
      const displayOwnershipPercentage = [securityTypes.UNISSUED_OPTIONS, securityTypes.CONVERTIBLE_NOTES].includes(
        column.security_type!
      );

      const parsedValue = parseValue(value, type, null, null, row.dbType, true);

      const referenceGeneratorService = new CapTableReferenceGenerator();

      const getReferencesForCell = (alias: string, referenceList: MappingReferenceData[]) => {
        const acceptedNames = [alias, ...(REFERENCE_FIELD_SYNONYMS_BY_ALIAS[alias] ?? [])];
        return referenceList.filter(item => acceptedNames.includes(item.field_name));
      };

      const cellReferences = (column as any).mapping_references
        ? getReferencesForCell(row.alias!, (column as any).mapping_references || [])
        : [];

      const cellCitationReferences = (column as any).citation_references
        ? getReferencesForCell(row.alias!, (column as any).citation_references || [])
        : [];

      const docRefsData = row.alias === 'name'
        ? referenceGeneratorService.getDocumentReferences((column as any)?.mapping_references || [])
        : { documentIds: [], metadata: [] };

      const newCell = {
        // Copy default props from the row config
        ...row,
        // Replace default props
        key,
        expr: getExpr(row.expr!, columnLegend, parsedValue),
        value: parsedValue,
        alias: row.alias || '',
        columnId: column.id,
        exprBuilderEnabled: row.exprBuilderEnabled ?? false,

        // Use its own security_ref if it exists, otherwise create a new one
        securityRef: ref,
        columnRef: ref,
        columnLegend,
        columnOrder: column.order!,
        linkedCellSymbols: getLinkedCells(row.linkedCellSymbols as any, columnLegend, alphabet),

        // Add mapping references
        mappingReferences: cellReferences,
        columnarReferences: docRefsData.documentIds,
        columnarReferencesMetadata: docRefsData.metadata,

        citationReferences: cellCitationReferences,

        aiFieldMetadata:
          (column as any).ai_finding?.field_metadata?.[AI_METADATA_KEY_BY_ALIAS[row.alias!] ?? row.alias] ?? null,
        aiAccuracyConfidence: (column as any).ai_finding?.accuracy_confidence ?? null,
        isAiGeneratedColumn: !!((column as any).ai_finding?.field_metadata?.name?.value) && !(column as any).ai_finding?.field_metadata?.name?.is_accepted,
      } as CapTableCell;
      cells[key] = newCell;

      const pageIsNotDisabledAndSecurityInFundOwnership = Boolean(
        !row.readOnly && column.fod_usage_details?.is_used_in_fod
      );

      switch (rowNumber) {
        case SECURITY_NAME_ROW_NUMBER:
          updateCellProp(cells, 'id', toString(column.id) || '', key);
          break;
        case SECURITY_TYPE_ROW_NUMBER:
          updateCellProp(cells, 'readOnly', pageIsNotDisabledAndSecurityInFundOwnership || row.readOnly, key);
          updateCellProp(
            cells,
            'tooltipMessages',
            tooltipForSecurityInFundOwnership(pageIsNotDisabledAndSecurityInFundOwnership, row.tooltipMessages),
            key
          );
          break;
        case INVESTMENT_DATE_ROW_NUMBER:
          updateCellProp(cells, 'dataSourceKey', `${columnLegend}${MULTIPLE_INVESTMENT_LEDGER_ROW_NUMBER}`, key);
          break;
        case SHARES_OUTSTANDING_ROW_NUMBER:
          updateCellProp(cells, 'dataSourceKey', `${columnLegend}${OPTIONS_LEDGER_ROW_NUMBER}`, key);
          handleConvertibleNotes(column, cells, key);
          break;
        case PIK_SHARES_ROW_NUMBER:
          handleSecurityType(securityType, columnLegend, cells, key);
          break;
        case CURRENT_OWNERSHIP_ROW_NUMBER:
          updateCellProp(
            cells,
            'expr',
            (displayOwnershipPercentage ? defaultValue : currentOwnershipExpr) as string,
            key
          );
          break;
        case FULLY_DILUTED_OWNERSHIP_ROW_NUMBER:
          updateCellProp(cells, 'expr', fullyDilutedExpr, key);
          break;
        case STRIKE_PRICE_ROW_NUMBER:
          updateCellProp(cells, 'dataSourceKey', `${columnLegend}${OPTIONS_LEDGER_ROW_NUMBER}`, key);
          break;
        case HAS_MULTIPLE_INVESTMENT_DATES_ROW_NUMBER:
          updateCellProp(cells, 'dataSourceKey', `${columnLegend}${MULTIPLE_INVESTMENT_LEDGER_ROW_NUMBER}`, key);
          break;
        case ACCRUED_DIVIDENDS_ROW_NUMBER:
          if (preferredWithVariableLiqPref) {
            handlePreferredStockWithVariableLiqPref(newCell, column, measurementDate, cells, key);
          } else if (preferredWithMultipleDividendRates) {
            handlePreferredStockWithMultipleDividendRates(cells, key);
          } else {
            updateCellProp(cells, 'expr', replaceYearFracBasis(newCell.expr as string), key);
          }
          break;
        case INITIAL_LIQUIDATION_PREFERENCE_ROW_NUMBER:
          handleInitialLiquidationPreference(
            preferredWithVariableLiqPref,
            newCell,
            column,
            measurementDate,
            cells,
            key
          );
          break;
        case TOTAL_PREFERENCE_ROW_NUMBER:
          handleTotalPreference(preferredWithVariableLiqPref, newCell, column, measurementDate, cells, key);
          break;
        case MULTIPLE_INVESTMENT_LEDGER_ROW_NUMBER:
          if (value === YES_STRING) {
            const sharesOutstanding = calcSharesOutstandingFromMultipleInvestments(value);
            const sharesOutstandingKey = `${columnLegend}${SHARES_OUTSTANDING_ROW_NUMBER}`;
            updateCellProp(cells, 'expr', toString(sharesOutstanding), sharesOutstandingKey);
          }
          break;
        case LIQUIDATION_PREFERENCE_LEDGER_ROW_NUMBER:
          if (shouldBreakCaseLiqPrefRow) {
            break;
          }
          influenceCellsWithInformationFromLiqPrefLedger({
            column,
            columnLegend,
            cells,
            hasLiquidationPreferenceLedger,
          });
          updateCellProp(
            cells,
            'expr',
            column[LIQUIDATION_PREFERENCE_LEDGER_ALIAS] as string,
            `${columnLegend}${LIQUIDATION_PREFERENCE_LEDGER_ROW_NUMBER}`
          );
          break;
        case DIVIDEND_RATE_LEDGER_ROW_NUMBER:
          if (!column[DIVIDEND_RATES]?.length) {
            break;
          }
          influenceCellsWithInformationFromDivRateLedger({
            column,
            columnLegend,
            cells,
          });

          updateCellProp(
            cells,
            'expr',
            column[DIVIDEND_RATES] as unknown as string,
            `${columnLegend}${DIVIDEND_RATE_LEDGER_ROW_NUMBER}`
          );
          break;
        default:
          break;
      }
      if (row.alias?.includes('fund_shares')) {
        updateFundShareValue(newCell, column)

      } else if (row.alias?.includes('fund_ownership_percentage')) {
        updateFundOwnershipPercentage(newCell, columns, rowConfig)
      } else if (row.alias === 'firm_shares') {
        updateFirmShare(newCell, rowConfig)
      } else if (row.alias === 'firm_ownership_percentage') {
        updateFirmOwnershipPercentage(newCell, rowConfig)
      } else if (row.alias?.includes('fund_liquidation_preference')) {
        updateFundLiqPref(newCell, rowConfig)
      } else if (row.alias === 'firm_liquidation_preference') {
        updateFirmLiqPref(newCell, rowConfig)
      }
    });
  });

  return cells;
};

export default capTableParser;
